import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight, Clock, GraduationCap, Heart, Rocket } from 'lucide-react';
type Role = 'learner' | 'parent' | 'teacher';
const ROLE_CONFIG: Record<Role, {
  icon: React.ReactNode;
  label: string;
  description: string;
  color: string;
  activeBg: string;
  activeBorder: string;
  activeText: string;
}> = {
  learner: {
    icon: <Rocket className="w-6 h-6" data-id="element-443" data-mp="vbkzrgv7lmdj" />,
    label: 'Leerling',
    description: 'Ik wil oefenen en leren!',
    color: 'text-orange-500',
    activeBg: 'bg-orange-50',
    activeBorder: 'border-orange-400',
    activeText: 'text-orange-700'
  },
  parent: {
    icon: <Heart className="w-6 h-6" data-id="element-444" data-mp="leztytbd878n" />,
    label: 'Ouder',
    description: 'Ik volg de voortgang van mijn kind',
    color: 'text-emerald-500',
    activeBg: 'bg-emerald-50',
    activeBorder: 'border-emerald-400',
    activeText: 'text-emerald-700'
  },
  teacher: {
    icon: <GraduationCap className="w-6 h-6" data-id="element-445" data-mp="hc37usmm9szy" />,
    label: 'Docent',
    description: 'Ik beheer mijn klas',
    color: 'text-indigo-500',
    activeBg: 'bg-indigo-50',
    activeBorder: 'border-indigo-400',
    activeText: 'text-indigo-700'
  }
};
interface RegisterRoleStepProps {
  role: Role;
  onSelectRole: (role: Role) => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterRoleStep({
  role,
  onSelectRole,
  slideVariants
}: RegisterRoleStepProps) {
  const itemVariants = {
    enter: {
      opacity: 0,
      y: 20
    },
    center: {
      opacity: 1,
      y: 0
    },
    exit: {
      opacity: 0,
      y: -20
    }
  };
  return <motion.div key="step-role" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3,
    staggerChildren: 0.1
  }} className="space-y-6" data-id="element-446" data-mp="onla15cqhj7i">
      
      <motion.h2 variants={itemVariants} className="text-xl font-black text-slate-900 text-center mb-2" data-id="element-447" data-mp="ay19852pqw0u">
        
        Wie ben jij?
      </motion.h2>

      {/* Learner Hero Card */}
      <motion.button variants={itemVariants} type="button" onClick={() => onSelectRole('learner')} className={`relative w-full p-6 rounded-3xl border-2 text-left font-bold transition-all overflow-hidden ${role === 'learner' ? 'border-orange-400 shadow-md scale-[1.02]' : 'border-orange-200 shadow-sm hover:border-orange-300 hover:shadow-md'}`} whileTap={{
      scale: 0.98
    }} data-id="element-448" data-mp="5x3s7vtfw80x">
        
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-100/50 -z-10" data-id="element-449" data-mp="2juovdmevtsyo" />
        <div className="absolute top-0 right-0 bg-orange-500 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-bl-xl rounded-tr-xl" data-id="element-450" data-mp="1n4eo0kvss7lp">
          Meest gekozen!
        </div>

        <div className="flex items-center gap-5" data-id="element-451" data-mp="1w54auz32ynna">
          <div className="w-16 h-16 rounded-2xl bg-white shadow-sm flex items-center justify-center text-orange-500 flex-shrink-0" data-id="element-452" data-mp="2135w9qnzaz1x">
            <Rocket className="w-8 h-8" data-id="element-453" data-mp="37hx4kffflm5z" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-454" data-mp="3cggrai1un1ze">
            <div className="text-2xl text-orange-900 mb-1" data-id="element-455" data-mp="3cff4xhfdut2d">Leerling</div>
            <div className="text-sm font-medium text-orange-700/80" data-id="element-456" data-mp="3mjvha0tnls1f">
              Ik wil oefenen en leren!
            </div>
          </div>
          <ChevronRight className={`w-6 h-6 flex-shrink-0 transition-transform ${role === 'learner' ? 'text-orange-600 translate-x-1' : 'text-orange-400'}`} data-id="element-457" data-mp="3hog5rfj4c886" />
          
        </div>
      </motion.button>

      <div className="relative flex items-center py-2" data-id="element-458" data-mp="38rz18v59f8le">
        <div className="flex-grow border-t border-slate-100" data-id="element-459" data-mp="33vi4jcjzvlwp"></div>
        <span className="flex-shrink-0 mx-4 text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-460" data-mp="2yv2scobg8kgb">
          Of ben je een
        </span>
        <div className="flex-grow border-t border-slate-100" data-id="element-461" data-mp="2tx17pyydkd2q"></div>
      </div>

      {/* Parent & Teacher Row */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 gap-3" data-id="element-462" data-mp="2px0n1wkon5n8">
        {(['parent', 'teacher'] as Role[]).map(r => {
        const cfg = ROLE_CONFIG[r];
        const isActive = role === r;
        return <motion.button key={r} type="button" onClick={() => onSelectRole(r)} className={`p-4 rounded-2xl border-2 text-center font-bold transition-all flex flex-col items-center gap-3 ${isActive ? `${cfg.activeBorder} ${cfg.activeBg} ${cfg.activeText} shadow-sm` : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`} whileTap={{
          scale: 0.96
        }} data-id="element-463" data-mp="o1q4v2itj0f7">
              
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isActive ? 'bg-white shadow-sm' : 'bg-slate-100'} ${isActive ? cfg.activeText : cfg.color}`} data-id="element-464" data-mp="q9iupib1pswq">
                
                {cfg.icon}
              </div>
              <div className="text-sm" data-id="element-465" data-mp="2jet1ghf438d">{cfg.label}</div>
            </motion.button>;
      })}
      </motion.div>

      {/*
       DE WACHTTIJD HOORT HIER, NIET NA HET INLOGGEN.
        De uitleg over goedkeuring stond in een banner die pas ná registreren
       verscheen. Wie dat niet wist, maakte een account, zag een leeg portaal en
       concludeerde dat de app stuk was. Een verwachting die je pas achteraf
       bijstelt, is geen verwachting maar een verrassing.
        Alleen bij een gekozen docentrol, niet als algemene voetnoot: een ouder of
       leerling hoeft dit niet te lezen, en een waarschuwing die voor iedereen
       geldt wordt door niemand gelezen.
        Bewust GEEN blokkade en geen "weet je het zeker". Registreren mag gewoon —
       ook een bijlesdocent hoort hier binnen te komen. Dit stelt alleen bij wat
       er ná het aanmaken gebeurt.
       */}
      {role === 'teacher' && <motion.div variants={itemVariants} role="status" className="flex gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 p-3.5" data-id="element-466" data-mp="3ozccqheg6avo">
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" data-id="element-467" data-mp="2katizoqv95o" />
          <div data-id="element-468" data-mp="2mnab9irndh5">
            <p className="text-sm font-bold text-amber-900" data-id="element-469" data-mp="gjso5voq1gmy">
              Een docentaccount geeft niet direct inzage
            </p>
            <p className="mt-0.5 text-sm leading-relaxed text-amber-900/90" data-id="element-470" data-mp="bm0js26w421v">
              Je kunt je nu aanmelden, maar gegevens van leerlingen blijven dicht
              tot je bent goedgekeurd. Werk je op een school, dan doen je
              collega's dat — of wij, als de school nog niet bij ons bekend is.
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-amber-900/90" data-id="element-471" data-mp="3ecca3fbe2wpm">
              Geef je bijles of thuisonderwijs? Dat kan ook, maar dan loopt de
              toegang via de ouder: die koppelt je aan zijn kind. Je krijgt dan
              geen eigen klassen.
            </p>
          </div>
        </motion.div>}
    </motion.div>;
}